import React, { useState, useEffect } from "react";

import { valueToString } from "helpers/Helpers";

import { useHistory } from "react-router-dom";

import Page from "components/page/Page";
import Card from "components/card/Card";


import { StatusCode } from "api/protocols";

import { useTranslation } from "react-i18next";
import { GetCardReaders } from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/reader";
import axios, { CancelToken } from "axios";
import DataTable from "../../../../../components/dataTable/DataTable";
import Input from "components/form/input/Input";
import DropFilter from "components/dropdown/DropFilter";
import { Badge } from "components/badge/Badge";
import Avatar from "components/avatar/Avatar";
import { TCardReader } from "redux/reducers/models/facility";

const TABLE_LIMIT = 50;

export default function CardReaders() {
  const history = useHistory();
  const { t } = useTranslation();
  
  function navigateToNewCardReader() {
    history.push("/admin/settings/card_reader/new");
  }

  function hardwareImage(type: string) {
    switch (type) {
      case "bbpos_wisepos_e":
        return "/public/images/hardware/stripe_wisepos.png";
      case "bbpos_wisepad_3":
        return "/public/images/hardware/stripe_wisepad.png";
      case "bbpos_wisepos_e_dock":
        return "/public/images/hardware/stripe_wisepos_dock.png";
      case "stripe_s700":
        return "/public/images/hardware/stripe_s700.png";
      case "stripe_s700_hub":
        return "/public/images/hardware/stripe_s700_hub.png";
      case "stripe_s700_dock":
        return "/public/images/hardware/stripe_s700_dock.png";
    }
  }

  function statusBadge(status: string) {
    switch (status) {
      case "online":
        return (
          <Badge size="small" type="success">
            {valueToString(status)}
          </Badge>
        );
      case "broken":
        return (
          <Badge size="small" type="error">
            {valueToString(status)}
          </Badge>
        );
      case "deployed":
        return (
          <Badge size="small" type="warning">
            {valueToString(status)}
          </Badge>
        );
      default:
        return status ? (
          <Badge size="small" type="gray">
            {valueToString(status)}
          </Badge>
        ) : null;
    }
  }

  const primaryAction = {
    content: t("secure.facility.settings.card_reader.card_readers.001"),
    action: navigateToNewCardReader,
  };

  function navigateTo(url: string) {
    history.push(url);
  }

  const [state, setState] = useState<{cardReaders: TCardReader[]}>({
    cardReaders: undefined,
  });

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    status: [],
  });

  // Load information need for page
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCardReaders(source.token);
    return () => source.cancel();
  }, [filterState.search, filterState.status]);

  async function loadCardReaders(token: CancelToken) {
    setState(prevState => ({ ...prevState, cardReaders: undefined }));

    const loadStatusFilter = [{ label: "status", value: filterState.status }];
    const useFilter = loadStatusFilter?.some(filter => filter?.value?.length > 0);
    
    const res = await GetCardReaders(
      {
        search: filterState.search,
        limit: TABLE_LIMIT,
        offset: filterState.offset,
        filters: useFilter ? loadStatusFilter : undefined,
      },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }

    if (res.status === StatusCode.OK) {
      setState(prevState => ({ ...prevState, cardReaders: res.data }));
    } else {
      setState(prevState => ({ ...prevState, cardReaders: [] }));
    }
  }

  function setHardwareStatusFilter(filterData: Array<Record<string, any>>) {
    // If no filters are applied, reset and indicate that no filters are wanted
    if (filterData.length === 0) {
      setFilterState(prev => ({ ...prev, status: [] }));
      return;
    }

    // Create an array of valid statuses from the filterForStatusHardwareList
    const validStatuses = filterData.map(status => status.value as string);

    // Set the filtered data into the filters state
    setFilterState(prev => ({ ...prev, status: validStatuses }));
  }

  const filterForStatusHardwareList = [
    { value: "online", title: "Online" },
    { value: "offline", title: "Offline" },
    { value: "inventory", title: t("Inventory") },
    { value: "broken", title: "Broken" },
    { value: "warranty", title: "Warranty" },
    { value: "deployed", title: "Deployed" },
  ];

  return (
    <Page
      title={t("secure.facility.settings.card_reader.card_readers.002")}
      subtitle={t("secure.facility.settings.card_reader.card_readers.003")}
      narrow
      primaryAction={primaryAction}
    >
      <div className="flex flex-row flex-wrap gap-4">
        <DropFilter
          title={"Status"}
          filterData={filterForStatusHardwareList}
          filterLabelPropFromData="title"
          filterIdPropFromData="value"
          filterType="Checkbox"
          applyFilters={setHardwareStatusFilter}
          save
          historyKey="hardware_all"
          leftAligned
        />
      </div>

      <div className="mb-2">
        <Input
          placeholder={"Search by serial number or facility name"}
          value={filterState.search}
          onChange={e => setFilterState(prev => ({ ...prev, search: e.target.value, offset: 0 }))}
        />
      </div>
      
      <Card>
        <Card.Section table="true">
          <DataTable
            columns={[
              { label: t("secure.facility.settings.card_reader.card_readers.004") },
              { label: t("secure.facility.settings.card_reader.card_readers.005") },
              { label: t("secure.facility.settings.card_reader.card_readers.006") },
              { label: "Status" },
            ]}
            loading={state.cardReaders === undefined}
            footer={{
              tableLimit: TABLE_LIMIT,
              tableOffset: filterState.offset,
              disableNextOffset: !state.cardReaders || !(state.cardReaders?.length === TABLE_LIMIT),
              handleTableOffset: direction =>
                setFilterState(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - TABLE_LIMIT : prev.offset + TABLE_LIMIT,
                })),
            }}
          >
            {state.cardReaders?.map((reader, index: number) => (
              <tr key={index} onClick={() => navigateTo("/admin/settings/card_reader/" + String(reader.id))} className="clickable">
                <td>
                  <div className="flex items-center gap-4">
                    <Avatar source={hardwareImage(reader.device_type)} size="medium" />
                    <span className="table-cell-text">
                      <p className="lead-text">{reader.label}</p>
                    </span>
                  </div>
                </td>
                <td>{valueToString(reader.device_type)}</td>
                <td>{reader.serial_number}</td>
                <td>{statusBadge(reader.status)}</td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>
    </Page>
  );
}